/**
 * InvoiceItemRow Component - T100
 *
 * Displays a single invoice item in a table row
 */
import { formatCurrency, formatNumber } from '@armard/shared';
import { unitTypeLabels, type InvoiceItem } from '../../hooks/useInvoices';

interface InvoiceItemRowProps {
  item: InvoiceItem;
  position: number;
  onEdit?: () => void;
  onDelete?: () => void;
  readOnly?: boolean;
}

export function InvoiceItemRow({
  item,
  position,
  onEdit,
  onDelete,
  readOnly,
}: InvoiceItemRowProps) {
  const hasDiscount = item.discountType && parseFloat(item.discountValue || '0') > 0;

  const formatDiscount = () => {
    if (!hasDiscount) return null;
    if (item.discountType === 'percent') {
      const value = parseFloat(item.discountValue || '0');
      return `-${formatNumber(value, Number.isInteger(value) ? 0 : 2)} %`;
    }
    return `-${formatCurrency(parseFloat(item.discountValue || '0'))}`;
  };

  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
      {/* Position */}
      <td className="py-3 px-2 text-white/40 text-center w-12">{position}</td>

      {/* Description */}
      <td className="py-3 px-3">
        <div className="text-white">{item.description}</div>
        {hasDiscount && (
          <div className="text-xs text-orange-400 mt-0.5">Positionsrabatt: {formatDiscount()}</div>
        )}
      </td>

      {/* Quantity & Unit */}
      <td className="py-3 px-3 text-right whitespace-nowrap">
        {item.unitType === 'flat_rate' && parseFloat(item.quantity) === 1 ? (
          <span className="text-white/30">&mdash;</span>
        ) : (
          <>
            <span className="text-white">{formatNumber(parseFloat(item.quantity))}</span>
            <span className="text-white/50 ml-1">{unitTypeLabels[item.unitType]}</span>
          </>
        )}
      </td>

      {/* Unit Price */}
      <td className="py-3 px-3 text-right text-white whitespace-nowrap">
        {formatCurrency(parseFloat(item.unitPrice))}
      </td>

      {/* Line Total */}
      <td className="py-3 px-3 text-right text-white font-medium whitespace-nowrap">
        {formatCurrency(parseFloat(item.lineTotal))}
      </td>

      {/* Actions */}
      {!readOnly && (
        <td className="py-3 px-2 text-right w-20">
          <div className="flex items-center justify-end gap-1">
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="min-h-11 min-w-11 p-2 text-white/40 hover:text-white hover:bg-white/5 rounded transition-colors"
                aria-label={`Position ${position} bearbeiten`}
                title="Bearbeiten"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="min-h-11 min-w-11 p-2 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                aria-label={`Position ${position} entfernen`}
                title="Löschen"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}
